import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { CreateAuditRecordDTO } from "@/modules/internal-audit/domain/contracts";
import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, HelpCircle } from "lucide-react";
import { useAuditPlanDetailQuery, useCreateAuditRecord } from "../api/queries";
import {
  AuditRecordFormTour,
  useAuditRecordFormTour,
} from "../tour/AuditRecordFormTour";
import { AuditRecordForm } from "../ui/AuditRecordForm";

export function AuditRecordCreatePage() {
  const { planId } = useParams({
    from: "/app/internal-audit/records/new/$planId",
  });
  const nav = useNavigate();
  const auditPlan = useAuditPlanDetailQuery(planId);
  const createMutation = useCreateAuditRecord();
  const tour = useAuditRecordFormTour();

  const handleBack = () => {
    nav({
      to: "/app/internal-audit/records",
      search: {
        offset: 0,
        limit: 20,
        sort: undefined,
        filters: undefined,
      },
    });
  };

  const handleSubmit = async (data: {
    auditPlanId: string;
    auditDate: Date;
    auditorId: string;
    auditScope: string;
    overallResult: "pass" | "fail" | "needs_improvement";
    summary?: string | null;
    status: "draft" | "completed";
    items: Array<{
      title: string;
      description?: string | null;
      orderIndex: number;
      result: "pass" | "fail" | "needs_improvement";
      notes?: string | null;
      mediaAssetId?: string | null;
    }>;
    attachments: string[];
  }) => {
    const createData: CreateAuditRecordDTO = {
      auditPlanId: data.auditPlanId,
      auditDate: data.auditDate,
      auditorId: data.auditorId,
      auditScope: data.auditScope,
      overallResult: data.overallResult,
      summary: data.summary,
      status: data.status,
      items: data.items.map((item) => ({
        title: item.title,
        description: item.description,
        orderIndex: item.orderIndex,
        result: item.result,
        notes: item.notes,
      })),
      attachments: data.attachments,
    };

    const result = await createMutation.mutateAsync(createData);
    // Navigate to record detail page after create
    // The API returns { value: AuditRecordDetailDTO }
    if (result?.value?.id) {
      nav({
        to: "/app/internal-audit/records/$id",
        params: { id: result.value.id },
      });
    } else {
      // Fallback: navigate to records list
      nav({
        to: "/app/internal-audit/records",
        search: {
          offset: 0,
          limit: 20,
          sort: undefined,
          filters: undefined,
        },
      });
    }
  };

  if (auditPlan.isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກໍາລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (!auditPlan.data) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] flex-col items-center justify-center">
            <p className="text-muted-foreground">ບໍ່ພົບຜະແນການກວດສອບ</p>
            <Button variant="outline" onClick={handleBack} className="mt-4">
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              ກັບໄປ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <AuditRecordFormTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div className="mb-6 flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={handleBack}>
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
          <div className="flex items-center space-x-2">
            <div>
              <h1 className="font-bold text-3xl">ສ້າງບັນທຶກການກວດສອບ</h1>
              <p className="mt-1 text-muted-foreground">
                ຜະແນການກວດສອບ: {auditPlan.data.name}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 rounded-full"
              onClick={tour.startTour}
            >
              <HelpCircle className="size-5" />
            </Button>
          </div>
        </div>

        <AuditRecordForm
          auditPlan={auditPlan.data}
          isEdit={false}
          onSubmit={handleSubmit}
          submitting={createMutation.isPending}
        />
      </Main>
    </>
  );
}
